import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { maintenanceAPI, unitAPI } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import {
  Plus,
  Wrench,
  AlertTriangle,
  Clock,
  CheckCircle,
  MessageSquare,
  Send,
  ChevronDown,
  ChevronUp,
  Crown,
  User,
} from 'lucide-react';
import SatisfactionSurvey from '../../components/SatisfactionSurvey';
import { useI18n } from '../../context/I18nContext';

const TenantMaintenance = () => {
  const { t } = useI18n();
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [expandedTicket, setExpandedTicket] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [formData, setFormData] = useState({
    unit_id: '',
    title: '',
    description: '',
    priority: 'MEDIUM'
  });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [ticketsRes, unitsRes] = await Promise.all([
        maintenanceAPI.getAll(),
        unitAPI.getAll()
      ]);
      setTickets(ticketsRes.data);
      setUnits(unitsRes.data);
      if (unitsRes.data.length === 1) {
        setFormData(prev => ({ ...prev, unit_id: unitsRes.data[0].id }));
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await maintenanceAPI.create(formData);
      toast.success('Maintenance request submitted');
      setDialogOpen(false);
      setFormData({ unit_id: units.length === 1 ? units[0].id : '', title: '', description: '', priority: 'MEDIUM' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    }
  };

  const handleSendMessage = async (ticketId) => {
    if (!newMessage.trim()) return;
    try {
      await maintenanceAPI.addMessage(ticketId, newMessage.trim());
      setNewMessage('');
      loadData();
    } catch (error) {
      toast.error('Failed to send message');
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'OPEN': return <AlertTriangle className="w-5 h-5 text-amber-400" />;
      case 'IN_PROGRESS': return <Clock className="w-5 h-5 text-blue-400" />;
      case 'RESOLVED': return <CheckCircle className="w-5 h-5 text-emerald-400" />;
      case 'CLOSED': return <CheckCircle className="w-5 h-5 text-gray-400" />;
      default: return <Wrench className="w-5 h-5" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'OPEN': return 'bg-amber-500/10 text-amber-400 border-amber-500/20';
      case 'IN_PROGRESS': return 'bg-blue-500/10 text-blue-400 border-blue-500/20';
      case 'RESOLVED': return 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20';
      case 'CLOSED': return 'bg-gray-500/10 text-gray-400 border-gray-500/20';
      default: return 'bg-gray-500/10 text-gray-400 border-gray-500/20';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'URGENT': return 'text-red-400';
      case 'HIGH': return 'text-orange-400';
      case 'MEDIUM': return 'text-amber-400';
      case 'LOW': return 'text-emerald-400';
      default: return 'text-gray-400';
    }
  };

  const activeTickets = tickets.filter(t => t.status !== 'CLOSED');
  const closedTickets = tickets.filter(t => t.status === 'CLOSED');

  const renderThread = (ticket) => {
    const messages = ticket.messages || [];
    const notes = ticket.notes || [];
    const all = [
      ...messages.map(m => ({ ...m, type: 'message' })),
      ...notes.map(n => ({ ...n, type: 'note' })),
    ].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

    return (
      <div className="mt-4 pt-4 border-t border-border space-y-3" data-testid={`ticket-thread-${ticket.id}`}>
        <p className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1">
          <MessageSquare className="w-3 h-3" />
          Conversation ({all.length})
        </p>
        <div className="space-y-2 max-h-60 overflow-y-auto">
          {all.map((entry) => {
            const isMe = entry.author_id === user?.id;
            return (
              <div key={entry.id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] p-3 ${isMe ? 'bg-gold/10 border border-gold/20' : 'bg-white border border-[#e5e7eb]'}`}>
                  <div className="flex items-center gap-2 mb-1">
                    {entry.author_role === 'LANDLORD' ? (
                      <Crown className="w-3 h-3 text-gold" />
                    ) : (
                      <User className="w-3 h-3 text-blue-400" />
                    )}
                    <span className="text-xs font-medium">{entry.author_name}</span>
                    <span className="text-[10px] text-muted-foreground">
                      {new Date(entry.created_at).toLocaleString()}
                    </span>
                  </div>
                  <p className="text-sm">{entry.text}</p>
                </div>
              </div>
            );
          })}
          {all.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-2">No messages yet</p>
          )}
        </div>
        {ticket.status !== 'CLOSED' && (
          <div className="flex gap-2">
            <Input
              value={expandedTicket === ticket.id ? newMessage : ''}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Type a message..."
              className="bg-white border-[#e5e7eb] text-sm"
              onKeyDown={(e) => e.key === 'Enter' && handleSendMessage(ticket.id)}
              data-testid={`message-input-${ticket.id}`}
            />
            <Button
              size="icon"
              className="bg-gold text-white hover:bg-gold-light shrink-0"
              onClick={() => handleSendMessage(ticket.id)}
              data-testid={`send-message-${ticket.id}`}
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <DashboardLayout type="tenant" title={t('tenant.maintenance')}>
        <div className="space-y-4">
          {[...Array(4)].map((_, i) => <div key={i} className="h-24 bg-card animate-pulse" />)}
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout type="tenant" title={t('tenant.maintenance')}>
      <div className="space-y-6" data-testid="tenant-maintenance-page">
        <div className="flex items-center justify-between">
          <p className="text-muted-foreground">Submit and track maintenance requests</p>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-gold text-white hover:bg-gold-light" data-testid="new-request-btn">
                <Plus className="w-4 h-4 mr-2" /> New Request
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px] bg-card border-border">
              <DialogHeader>
                <DialogTitle className="font-serif">Submit Maintenance Request</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                {units.length > 1 && (
                  <div className="space-y-2">
                    <Label className="text-muted-foreground text-sm uppercase tracking-wider">Unit</Label>
                    <Select value={formData.unit_id} onValueChange={(v) => setFormData({...formData, unit_id: v})}>
                      <SelectTrigger className="bg-background border-[#e5e7eb]" data-testid="request-unit-select">
                        <SelectValue placeholder="Select unit" />
                      </SelectTrigger>
                      <SelectContent>
                        {units.map(u => <SelectItem key={u.id} value={u.id}>Unit {u.unit_number}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="space-y-2">
                  <Label className="text-muted-foreground text-sm uppercase tracking-wider">Issue Title</Label>
                  <Input name="title" value={formData.title} onChange={handleChange} className="bg-background border-[#e5e7eb]" placeholder="e.g., Leaky faucet in bathroom" required data-testid="request-title-input" />
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground text-sm uppercase tracking-wider">Priority</Label>
                  <Select value={formData.priority} onValueChange={(v) => setFormData({...formData, priority: v})}>
                    <SelectTrigger className="bg-background border-[#e5e7eb]" data-testid="request-priority-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="LOW">Low - Can wait</SelectItem>
                      <SelectItem value="MEDIUM">Medium - Needs attention soon</SelectItem>
                      <SelectItem value="HIGH">High - Urgent</SelectItem>
                      <SelectItem value="URGENT">Urgent - Emergency</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground text-sm uppercase tracking-wider">Description</Label>
                  <Textarea name="description" value={formData.description} onChange={handleChange} className="bg-background border-[#e5e7eb] min-h-[100px]" placeholder="Please describe the issue in detail..." required data-testid="request-description-input" />
                </div>
                <Button type="submit" className="w-full bg-gold text-white hover:bg-gold-light" data-testid="submit-request-btn">Submit Request</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Active Tickets */}
        <div>
          <h2 className="font-serif text-xl mb-4">Active Requests</h2>
          {activeTickets.length === 0 ? (
            <Card className="bg-card border-border">
              <CardContent className="py-8 text-center">
                <Wrench className="w-12 h-12 mx-auto mb-3 text-muted-foreground opacity-50" />
                <p className="text-muted-foreground">No active maintenance requests</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {activeTickets.map((ticket, index) => (
                <Card key={ticket.id} className="bg-card border-border hover:border-gold/30 transition-all duration-300 card-animate" style={{ animationDelay: `${index * 0.05}s` }} data-testid={`active-ticket-${ticket.id}`}>
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      {getStatusIcon(ticket.status)}
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="font-medium">{ticket.title}</h3>
                            <span className={`px-2 py-0.5 text-xs uppercase tracking-wider border ${getStatusColor(ticket.status)}`}>{ticket.status.replace('_', ' ')}</span>
                          </div>
                          <button
                            onClick={() => setExpandedTicket(expandedTicket === ticket.id ? null : ticket.id)}
                            className="text-muted-foreground hover:text-foreground transition-colors"
                            data-testid={`toggle-thread-${ticket.id}`}
                          >
                            {expandedTicket === ticket.id ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                          </button>
                        </div>
                        <p className="text-sm text-muted-foreground mb-3">{ticket.description}</p>
                        <div className="flex items-center gap-4 text-sm">
                          <span className={`font-medium ${getPriorityColor(ticket.priority)}`}>{ticket.priority} Priority</span>
                          <span className="text-muted-foreground">Submitted: {new Date(ticket.created_at).toLocaleDateString()}</span>
                          <button
                            onClick={() => setExpandedTicket(expandedTicket === ticket.id ? null : ticket.id)}
                            className="flex items-center gap-1 text-gold hover:underline"
                            data-testid={`view-thread-${ticket.id}`}
                          >
                            <MessageSquare className="w-3 h-3" />
                            {(ticket.messages || []).length + (ticket.notes || []).length} messages
                          </button>
                        </div>
                        {expandedTicket === ticket.id && renderThread(ticket)}
                        {(ticket.status === 'RESOLVED' || ticket.status === 'CLOSED') && (
                          <SatisfactionSurvey
                            ticketId={ticket.id}
                            ticketTitle={ticket.title}
                            onComplete={() => loadData()}
                          />
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Closed */}
        {closedTickets.length > 0 && (
          <div>
            <h2 className="font-serif text-xl mb-4">Resolved Requests</h2>
            <div className="space-y-3">
              {closedTickets.map((ticket) => (
                <Card key={ticket.id} className="bg-card border-border" data-testid={`closed-ticket-${ticket.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <CheckCircle className="w-5 h-5 text-gray-400" />
                        <div>
                          <p className="font-medium">{ticket.title}</p>
                          <p className="text-sm text-muted-foreground">Resolved: {ticket.resolved_at ? new Date(ticket.resolved_at).toLocaleDateString() : 'N/A'}</p>
                        </div>
                      </div>
                      <span className="px-2 py-0.5 text-xs uppercase tracking-wider bg-gray-500/10 text-gray-400 border border-gray-500/20">CLOSED</span>
                    </div>
                    <SatisfactionSurvey
                      ticketId={ticket.id}
                      ticketTitle={ticket.title}
                      onComplete={() => loadData()}
                    />
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default TenantMaintenance;
